import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, ClipboardList } from 'lucide-react';
import api from '@/lib/api';
import type { SharedTask, SharedTaskCategory } from '@/types';
import { TaskItem } from './TaskItem';
import { AddTaskForm } from './AddTaskForm';

const STATUS_TABS = [
  { value: 'todo,in_progress', label: 'Active' },
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
] as const;

const CATEGORY_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'household', label: 'Household' },
  { value: 'kai_care', label: "Kai's Care" },
  { value: 'shopping', label: 'Shopping' },
  { value: 'medical', label: 'Medical' },
  { value: 'general', label: 'General' },
];

export function TaskList() {
  const [statusFilter, setStatusFilter] = useState('todo,in_progress');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Fetch tasks
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['shared-tasks', statusFilter, categoryFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ status: statusFilter });
      if (categoryFilter) params.set('category', categoryFilter);
      const res = await api.get(`/shared-tasks?${params}`);
      return (res.data.tasks || []) as SharedTask[];
    },
    staleTime: 1000 * 10,
    refetchInterval: 15000,
  });

  // Fetch counts for badges
  const { data: counts = { todo: 0, in_progress: 0, done: 0 } } = useQuery({
    queryKey: ['shared-tasks-counts'],
    queryFn: async () => {
      const res = await api.get('/shared-tasks/counts');
      return res.data.counts as Record<string, number>;
    },
    staleTime: 1000 * 10,
    refetchInterval: 15000,
  });

  const getTabCount = (value: string) => {
    if (value === 'todo,in_progress') return (counts.todo || 0) + (counts.in_progress || 0);
    return counts[value] || 0;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-xl border border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList size={20} className="text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Tasks</h2>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <Plus size={16} />
            Add Task
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAddForm && (
        <AddTaskForm onClose={() => setShowAddForm(false)} />
      )}

      {/* Status tabs */}
      <div className="flex gap-1 bg-card rounded-xl border border-slate-700 p-1.5">
        {STATUS_TABS.map((tab) => {
          const count = getTabCount(tab.value);
          const isActive = statusFilter === tab.value;
          return (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                isActive
                  ? 'bg-slate-700 text-white font-medium'
                  : 'text-gray-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  isActive ? 'bg-blue-500 text-white' : 'bg-slate-700 text-gray-400'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Category filter pills */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {CATEGORY_FILTERS.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setCategoryFilter(cat.value)}
            className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
              categoryFilter === cat.value
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                : 'bg-slate-800 text-gray-400 border border-slate-700 hover:text-white'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Task list */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading tasks...</div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400 mb-2">No tasks here</p>
            <p className="text-xs text-gray-500">
              {statusFilter.includes('todo') ? 'Add a task to get started' : 'Nothing to show for this filter'}
            </p>
          </div>
        ) : (
          tasks.map((task) => (
            <TaskItem key={task.id} task={task} />
          ))
        )}
      </div>
    </div>
  );
}
